// src/AuthScreen.js
import React, { useState, useEffect, useCallback } from 'react';
import { B } from './constants';
import { GBWordmark } from './components/Logo';
import { useAuth } from './AuthContext';
import { trackEvent } from './lib/analytics';
import { trackActivity } from './lib/activityEvents';
import Input from './components/ui/Input';
import { auth } from './api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const backgrounds = [
  'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80',
  'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1200&q=80',
  'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&q=80',
];

const darkInputStyle = { background:'rgba(255,255,255,0.06)', color:'white', border:'1.5px solid rgba(255,255,255,0.1)' };
const darkLabel = { display:'block', marginBottom:7, color:'#aaa', fontSize:12, fontWeight:700, letterSpacing:0.5, textTransform:'uppercase' };
const secondaryButtonStyle = { width:'100%', background:'rgba(255,255,255,0.1)', color:'white', border:'1px solid rgba(255,255,255,0.25)', padding:'12px 0', borderRadius:12, cursor:'pointer', fontSize:13, fontWeight:700 };

// ── Load Google GSI script once ──────────────────────
function loadGoogleScript(callback) {
  if (window.google?.accounts) { callback(); return; }
  const existing = document.getElementById('google-gsi');
  if (existing) { existing.addEventListener('load', callback); return; }
  const script = document.createElement('script');
  script.id  = 'google-gsi';
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  script.onload = callback;
  document.head.appendChild(script);
}

export default function AuthScreen({ onSuccess, goHome }) {
  const [mode,      setMode]      = useState('login');
  const [bgIndex,   setBgIndex]   = useState(0);
  const [name,      setName]      = useState('');
  const [username,  setUsername]  = useState('');
  const [email,     setEmail]     = useState('');
  const [password,  setPassword]  = useState('');
  const [showPass,  setShowPass]  = useState(false);
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [oauthLoad, setOauthLoad] = useState('');  // 'google' | ''
  const [signupEmail, setSignupEmail] = useState(null);   // post-signup "check your email" panel
  const [unverifiedEmail, setUnverifiedEmail] = useState(null); // login 403 panel
  const [resendState, setResendState] = useState('');      // 'sending' | 'sent' | ''

  const { login, signup, googleLogin } = useAuth();

  // ── Google credential callback ───────────────────────
  const handleGoogleCredential = useCallback(async (response) => {
    setOauthLoad('google');
    setError('');
    try {
      const u = await googleLogin(response.credential);
      trackActivity('auth', 'login_completed', u?.id ? { userId: u.id } : {});
      onSuccess();
    } catch (err) {
      setError(err.message || 'Google sign-in failed');
    } finally {
      setOauthLoad('');
    }
  }, [googleLogin, onSuccess]);

  // ── Init Google One Tap ──────────────────────────────
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    loadGoogleScript(() => {
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
    });
  }, [handleGoogleCredential]);

  // ── Trigger Google popup ─────────────────────────────
  const handleGoogleClick = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google Client ID not configured. See setup guide below.');
      return;
    }
    setError('');
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        // Fallback: open Google OAuth popup
        window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'email profile',
          callback: async (tokenResponse) => {
            if (tokenResponse.error) { setError('Google sign-in cancelled'); return; }
            // Exchange access token for user info then send to our backend
            try {
              setOauthLoad('google');
              const u = await googleLogin(tokenResponse.access_token, 'access_token');
              trackActivity('auth', 'login_completed', u?.id ? { userId: u.id } : {});
              onSuccess();
            } catch (err) {
              setError(err.message || 'Google sign-in failed');
            } finally {
              setOauthLoad('');
            }
          },
        }).requestAccessToken({ prompt: 'select_account' });
      }
    });
  };

  // ── Email form submit ────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate before setting loading state
    if (mode === 'signup') {
      if (!name.trim())        { setError('Please enter your name'); return; }
      if (!/^[a-z0-9_.]{3,20}$/.test(username.trim().replace(/^@+/, '').toLowerCase())) {
        setError('Username must be 3-20 characters: letters, numbers, underscores or dots'); return;
      }
      if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
      else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(password)) {
        setError('Password must include uppercase, lowercase, a number, and a symbol'); return;
      }
    }
    
    setLoading(true);
    if (mode === 'signup') trackActivity('auth', 'signup_started', {});
    try {
      if (mode === 'signup') {
        await signup(name.trim(), email.trim(), password, username.trim().replace(/^@+/, '').toLowerCase());
        trackEvent('signup');
        // Our signup returns only a neutral message (no user object), so no userId here.
        trackActivity('auth', 'signup_completed', {});
        setSignupEmail(email.trim());
        setLoading(false);
        return; // no onSuccess — the account is not usable until verified
      }
      const u = await login(email.trim(), password);
      trackEvent('login');
      trackActivity('auth', 'login_completed', u?.id ? { userId: u.id } : {});
      onSuccess();
    } catch (err) {
      if (err.code === 'EMAIL_NOT_VERIFIED') {
        setUnverifiedEmail(email.trim());
      } else {
        setError(err.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    const emailAddr = signupEmail || unverifiedEmail;
    if (!emailAddr) return;
    setResendState('sending');
    try {
      await auth.resendVerification(emailAddr);
      setResendState('sent');
    } catch (err) {
      setError(err.message || 'Could not resend the email');
      setResendState('');
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setBgIndex(i => (i + 1) % backgrounds.length);
  };

  const noGoogleKey = !GOOGLE_CLIENT_ID;

  return (
    <div style={{ minHeight:'100vh', display:'flex', fontFamily:"'Montserrat',sans-serif" }}>
      <style>{`
        @keyframes imgFade { from{opacity:0;transform:scale(1.05)} to{opacity:1;transform:scale(1)} }
        @keyframes fadeUp  { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:0%} 100%{background-position:200%} }
        @keyframes spin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @media(min-width:768px){.auth-left{display:block!important}}
        input:-webkit-autofill{-webkit-box-shadow:0 0 0 30px #1a2332 inset!important;-webkit-text-fill-color:white!important}
        input:focus{border-color:${B.mint}!important}
        .oauth-btn:hover{background:rgba(255,255,255,0.12)!important;transform:translateY(-1px)}
        .oauth-btn:active{transform:translateY(0)}
      `}</style>

      {/* ── Left image panel ── */}
      <div className="auth-left" style={{ flex:'1 1 55%', position:'relative', overflow:'hidden', display:'none' }}>
        <img src={backgrounds[bgIndex]} alt="" key={bgIndex}
          style={{ width:'100%', height:'100%', objectFit:'cover', animation:'imgFade 0.8s ease' }} />
        <div style={{ position:'absolute', inset:0, background:'linear-gradient(160deg,rgba(15,25,35,0.55),rgba(26,107,74,0.4))' }} />
        <div style={{ position:'absolute', bottom:48, left:48, right:48 }}>
          <div style={{ width:40, height:3, background:B.amber, borderRadius:2, marginBottom:18 }} />
          <p style={{ fontFamily:"'Playfair Display',serif", fontSize:26, color:'white', fontWeight:700, lineHeight:1.4, marginBottom:12 }}>
            "The world is a book, and those who do not travel read only one page."
          </p>
          <p style={{ fontSize:12, color:'rgba(255,255,255,0.55)', letterSpacing:2, textTransform:'uppercase' }}>— Saint Augustine</p>
        </div>
        <div style={{ position:'absolute', top:32, left:48, display:'flex', gap:8 }}>
          {backgrounds.map((_, i) => (
            <div key={i} style={{ width:i===bgIndex?24:8, height:8, borderRadius:4, background:i===bgIndex?B.amber:'rgba(255,255,255,0.35)', transition:'all 0.4s' }} />
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{ flex:'1 1 45%', background:`linear-gradient(135deg,${B.night},#1a2e3f)`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'40px clamp(16px, 4vw, 48px)', minHeight:'100vh', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', inset:0, opacity:0.03, backgroundImage:'radial-gradient(circle,white 1px,transparent 1px)', backgroundSize:'28px 28px' }} />
        <div style={{ position:'absolute', inset:0, opacity:0.05, backgroundImage:`url("https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&q=30")`, backgroundSize:'cover', backgroundPosition:'center', filter:'blur(20px)' }} />
        <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${B.forest},${B.mint},${B.amber},${B.forest})`, backgroundSize:'200% 100%', animation:'shimmer 4s linear infinite' }} />

        <div style={{ width:'100%', maxWidth:400, position:'relative', animation:'fadeUp 0.5s ease', background:'rgba(15,25,35,0.75)', backdropFilter:'blur(16px)', WebkitBackdropFilter:'blur(16px)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:20, padding:'36px 32px' }}>

          {/* Logo */}
          <div style={{ marginBottom:36, display:'flex', justifyContent:'center' }}>
            <button onClick={goHome} style={{ background:'none', border:'none', cursor:'pointer', padding:0 }}>
              <GBWordmark size="md" />
            </button>
          </div>

          {/* Tab switcher */}
          <div style={{ display:'flex', background:'rgba(255,255,255,0.06)', borderRadius:14, padding:4, marginBottom:28 }}>
            {['login','signup'].map(m => (
              <button key={m} onClick={() => switchMode(m)} style={{ flex:1, padding:'11px 0', borderRadius:10, border:'none', cursor:'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:13, fontWeight:700, background:mode===m?'white':'transparent', color:mode===m?B.night:'#888', transition:'all 0.25s' }}>
                {m==='login'?'🔑 Sign In':'✨ Sign Up'}
              </button>
            ))}
          </div>

          {/* ── OAuth Buttons ── */}
          <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>

            {/* Google */}
            <button className="oauth-btn" onClick={handleGoogleClick} disabled={!!oauthLoad}
              style={{ width:'100%', background:'rgba(255,255,255,0.07)', border:'1.5px solid rgba(255,255,255,0.12)', borderRadius:12, padding:'13px 20px', cursor:oauthLoad?'not-allowed':'pointer', color:'white', fontSize:14, fontWeight:600, fontFamily:"'Montserrat',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:12, transition:'all 0.2s', position:'relative' }}>
              {oauthLoad==='google' ? (
                <div style={{ width:18, height:18, borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white', animation:'spin 0.7s linear infinite' }} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#FFC107" d="M43.6 20H24v8h11.3C33.6 33.1 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.7-.1-4z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.1 29.2 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-1.9 13.4-5.1l-6.2-5.2C29.3 35.3 26.8 36 24 36c-5.3 0-9.6-2.9-11.3-7l-6.6 5.1C9.7 39.7 16.3 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20H24v8h11.3c-.9 2.4-2.5 4.4-4.6 5.8l6.2 5.2C40.8 35.5 44 30.2 44 24c0-1.3-.1-2.7-.4-4z"/>
                </svg>
              )}
              {oauthLoad==='google' ? 'Signing in with Google...' : 'Continue with Google'}
            </button>

          </div>

          {/* Setup notice if keys missing */}
          {noGoogleKey && (
            <div style={{ background:'rgba(245,166,35,0.1)', border:'1px solid rgba(245,166,35,0.25)', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#c8872a', lineHeight:1.6 }}>
              ⚙️ <strong>Dev mode:</strong> Add <code style={{ background:'rgba(255,255,255,0.1)', padding:'1px 5px', borderRadius:4 }}>REACT_APP_GOOGLE_CLIENT_ID</code> to your <code style={{ background:'rgba(255,255,255,0.1)', padding:'1px 5px', borderRadius:4 }}>.env</code> to enable OAuth. <span style={{ color:B.mint, cursor:'pointer', fontWeight:700 }} onClick={() => document.getElementById('oauth-guide')?.scrollIntoView({ behavior:'smooth' })}>See guide ↓</span>
            </div>
          )}

          {/* Divider */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20 }}>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.08)' }} />
            <span style={{ color:'#555', fontSize:12 }}>or use email</span>
            <div style={{ flex:1, height:1, background:'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Heading */}
          <div style={{ marginBottom:20 }}>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, color:'white', fontWeight:700, marginBottom:4 }}>
              {mode==='login'?'Welcome back!':'Create account'}
            </h1>
            <p style={{ color:'#666', fontSize:13 }}>
              {mode==='login'?'Sign in with your email.':'Fill in your details below.'}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background:'rgba(220,53,69,0.15)', border:'1px solid rgba(220,53,69,0.3)', borderRadius:10, padding:'12px 16px', marginBottom:16, color:'#ff6b7a', fontSize:13, display:'flex', alignItems:'center', gap:8, animation:'fadeUp 0.3s ease' }}>
              ⚠️ {error}
            </div>
          )}

          {/* Email Form */}
          {signupEmail ? (
            <div style={{ textAlign:'center', color:'white' }}>
              <h2 style={{ fontSize: 18, marginBottom: 12 }}>Check your email</h2>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 20 }}>
                We sent a verification link to <strong>{signupEmail}</strong>. Click it to activate your account, then sign in.
              </p>
              {resendState === 'sent' && <p style={{ color:'#4ade80', fontSize: 13, marginBottom: 12 }}>Verification email sent again.</p>}
              <button type="button" onClick={handleResend} disabled={resendState === 'sending'}
                style={{ ...secondaryButtonStyle, marginBottom: 10 }}>
                {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
              </button>
              <div>
                <button type="button" onClick={() => { setSignupEmail(null); setResendState(''); }}
                  style={{ ...secondaryButtonStyle, background:'transparent' }}>
                  Back to sign in
                </button>
              </div>
            </div>
          ) : unverifiedEmail ? (
            <div style={{ textAlign:'center', color:'white' }}>
              <h2 style={{ fontSize: 18, marginBottom: 12 }}>Verify your email first</h2>
              <p style={{ color:'rgba(255,255,255,0.7)', fontSize: 14, marginBottom: 20 }}>
                {unverifiedEmail} isn't verified yet. We can send a fresh verification link.
              </p>
              {resendState === 'sent' && <p style={{ color:'#4ade80', fontSize: 13, marginBottom: 12 }}>Verification email sent.</p>}
              <button type="button" onClick={handleResend} disabled={resendState === 'sending'}
                style={{ ...secondaryButtonStyle, marginBottom: 10 }}>
                {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
              </button>
              <div>
                <button type="button" onClick={() => { setUnverifiedEmail(null); setResendState(''); }}
                  style={{ ...secondaryButtonStyle, background:'transparent' }}>
                  Back to sign in
                </button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {mode==='signup' && (
              <div style={{ animation:'fadeUp 0.3s ease' }}>
                <label style={darkLabel}>Full Name</label>
                <Input type="text" placeholder="Your full name" value={name} onChange={e=>setName(e.target.value)} icon="👤" error={!!error} style={darkInputStyle} required />
              </div>
            )}
            {mode==='signup' && (
              <div style={{ animation:'fadeUp 0.3s ease' }}>
                <label style={darkLabel}>Username</label>
                <Input type="text" placeholder="@yourhandle" value={username} onChange={e=>setUsername(e.target.value)} icon="@" error={!!error} style={darkInputStyle} autoCapitalize="none" maxLength={21} required />
              </div>
            )}
            <div>
              <label style={darkLabel}>Email</label>
              <Input type="email" placeholder="you@example.com" value={email} onChange={e=>setEmail(e.target.value)} icon="✉️" error={!!error} style={darkInputStyle} required />
            </div>
            <div>
              <label style={darkLabel}>Password</label>
              <div style={{ position:'relative', display:'flex', alignItems:'center' }}>
                <Input type={showPass?'text':'password'} placeholder={mode==='signup'?'At least 6 characters':'Your password'} value={password} onChange={e=>setPassword(e.target.value)} icon="🔒" error={!!error} style={{ ...darkInputStyle, paddingRight:44 }} required />
                <button type="button" onClick={()=>setShowPass(!showPass)} style={{ position:'absolute', right:14, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:16, color:'#666', padding:0 }}>
                  {showPass?'🙈':'👁️'}
                </button>
              </div>
              {mode === 'signup' && (
                <p style={{ color:'rgba(255,255,255,0.5)', fontSize:12, marginTop:6 }}>
                  Min 8 chars: at least one uppercase, lowercase, number, symbol
                </p>
              )}
            </div>

<button type="submit" disabled={loading||!!oauthLoad} style={{ background:loading?'#333':`linear-gradient(135deg,${B.forest},#22a06b)`, color:'white', border:'none', padding:'14px 0', borderRadius:12, cursor:loading?'not-allowed':'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:14, fontWeight:700, transition:'all 0.2s', boxShadow:loading?'none':`0 4px 20px rgba(26,107,74,0.4)` }}>
              {loading ? '⏳ Please wait...' : mode==='login' ? '✈️ Sign In' : '🚀 Create Account'}
            </button>
          </form>
          )}

            {mode === 'login' && (
              <p style={{ textAlign:'center', marginTop:16, fontSize:13 }}>
                <a href="#forgot" onClick={(e) => { e.preventDefault(); window.location.href = '/forgot-password'; }} style={{ color:'#666', textDecoration:'underline', cursor:'pointer' }}>
                  Forgot Password?
                </a>
              </p>
            )}

            <p style={{ textAlign:'center', marginTop:24, color:'#666', fontSize:13 }}>
              {mode==='login'?"Don't have an account? ":"Already have an account? "}
              <span onClick={()=>switchMode(mode==='login'?'signup':'login')} style={{ color:B.mint, cursor:'pointer', fontWeight:700 }}>
                {mode==='login'?'Sign up free':'Sign in'}
              </span>
            </p>

          {/* ── Setup Guide (only in dev mode when keys missing) ── */}
          {noGoogleKey && (
          <div id="oauth-guide" style={{ marginTop:36, padding:'20px', background:'rgba(255,255,255,0.03)', borderRadius:16, border:'1px solid rgba(255,255,255,0.07)', fontSize:12, color:'#666', lineHeight:1.8 }}>
            <p style={{ color:'#888', fontWeight:700, marginBottom:12, fontSize:13 }}>🔧 OAuth Setup Guide</p>

            <p style={{ color:B.mint, fontWeight:700, marginBottom:4 }}>Google:</p>
            <ol style={{ paddingLeft:16, marginBottom:14 }}>
              <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" style={{ color:B.amber }}>console.cloud.google.com</a></li>
              <li>APIs & Services → Credentials → Create OAuth 2.0 Client</li>
              <li>Add <code style={{ color:'white' }}>http://localhost:3000</code> to Authorized JS Origins</li>
              <li>Copy Client ID → add to <code style={{ color:'white' }}>frontend/.env</code>:<br/>
                <code style={{ color:B.amber, display:'block', marginTop:4 }}>REACT_APP_GOOGLE_CLIENT_ID=xxx.apps.googleusercontent.com</code>
              </li>
              <li>Add to <code style={{ color:'white' }}>backend/.env</code>:<br/>
                <code style={{ color:B.amber, display:'block', marginTop:4 }}>GOOGLE_CLIENT_ID=xxx.apps.googleusercontent.com</code>
              </li>
            </ol>


          </div>
          )}

        </div>
      </div>
    </div>
  );
}
